/**
 * localApi.js — 零后端演示版的本地数据层
 *
 * 设计说明：
 *  1) 把原项目 server/ 的全部逻辑（药品CRUD、提醒调度、记录统计、徽章、AI规则校验）
 *     平移到浏览器端，数据存 localStorage。对外暴露的方法名与返回结构与原 REST API
 *     完全一致，因此页面组件无需改动。
 *  2) 账号体系同样是「真」的：注册 / 登录 / 改密 / 退出都在本地完成，
 *     每个账号一份独立的 localStorage 数据，互不可见 —— 与正式版的服务端隔离行为一致。
 *     区别只在于：正式版凭证存在服务端并有 JWT 校验，这里只做本地档案区分，不具备安全性。
 *
 * 与原后端的唯一能力差异：提醒只在页面打开时检查（原版由后端 cron 每分钟扫描）。
 * 这是零后端方案的固有限制，已在界面上明确标注。
 */

import { matchKnownDrug, extractDrugName } from './drugDatabase';

const ACCOUNTS_KEY = 'mr_demo_accounts_v1';
// v3：新增 reminder_logs 与 seq.log。旧档（v2）没有提醒日志，日志页会是空的，
// 升版本让访问者重新拿到带完整「触发→送达→结果」日志链的种子数据。
const DB_PREFIX = 'mr_demo_db_v3:';
const SESSION_KEY = 'mr_demo_session_v1';

/** 一键体验用的演示账号（登录页会显式给出，降低作品集访问者的门槛） */
export const DEMO_CREDENTIALS = { username: 'demo', password: 'demo123' };

export const isDemoMode = true;

/* ------------------------------------------------------------------ *
 * 通用工具
 * ------------------------------------------------------------------ */

const pad = n => String(n).padStart(2, '0');
const fmtDate = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayStr = () => fmtDate(new Date());
const nowTimeStr = () => {
  const d = new Date();
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const shiftDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d;
};
const clone = o => JSON.parse(JSON.stringify(o));

/** 提醒状态机用到的常量，与 server/src/services/reminderScheduler.js 保持一致 */
const SNOOZE_MINUTES = 10;
const MAX_SNOOZE = 3;
const MISS_GRACE_MINUTES = 15;

/** 事件类型 → 显示文案。服务端由 reminderLog.js 统一下发，浏览器版在这里映射 */
const LOG_EVENT_TYPES = {
  triggered: { label: '到点提醒', icon: '⏰' },
  delivered: { label: '已送达设备', icon: '📱' },
  snoozed: { label: '稍后提醒', icon: '⏳' },
  confirmed: { label: '已确认服用', icon: '✅' },
  skipped: { label: '已跳过', icon: '⏭️' },
  missed: { label: '判定漏服', icon: '⚠️' },
};

/** 当前时刻往后推 N 分钟 */
function shiftMinutes(minutes) {
  return new Date(Date.now() + minutes * 60 * 1000);
}

/** 本地时间戳 YYYY-MM-DD HH:MM:SS（不用 toISOString，那是 UTC） */
function stamp(date = new Date()) {
  return `${fmtDate(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/**
 * 提醒日志：记「系统这一侧发生了什么」，与 records（记结果）分工不同。
 * 详细说明见 server/src/services/reminderLog.js。
 */
function logEvent(db, { reminderId = null, medicineId = null, medicineName = '', scheduledTime = '', event, detail = '', logDate, logTime }) {
  if (!LOG_EVENT_TYPES[event]) return;
  const now = new Date();
  db.reminder_logs.push({
    id: ++db.seq.log,
    user_id: 1,
    reminder_id: reminderId,
    medicine_id: medicineId,
    medicine_name: medicineName,
    scheduled_time: scheduledTime,
    event,
    detail,
    // 种子数据要写入历史日期的日志，所以允许外部指定时间
    log_date: logDate || fmtDate(now),
    log_time: logTime || `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`,
  });
}

const dbKey = username => `${DB_PREFIX}${username}`;

/* ------------------------------------------------------------------ *
 * 账号与登录态（本地档案隔离）
 * ------------------------------------------------------------------ */

function readAccounts() {
  try {
    const parsed = JSON.parse(localStorage.getItem(ACCOUNTS_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
}

function writeAccounts(accounts) {
  localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
}

/**
 * 本地口令摘要。
 *
 * 明确声明：这不是安全哈希，只是把明文口令从 localStorage 里挪开而已。
 * 演示版的定位是「让访问者能体验多账号数据隔离」，不具备任何真实防护能力；
 * 正式版走服务端 pbkdf2（见 server/src/middleware/auth.js）。
 */
function hashPassword(username, password) {
  const input = `mr-demo::${username}::${password}`;
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < input.length; i++) {
    const c = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 16777619) >>> 0;
    h2 = Math.imul(h2 + c * (i + 7), 2654435761) >>> 0;
  }
  return `${h1.toString(16).padStart(8, '0')}${h2.toString(16).padStart(8, '0')}`;
}

function readSession() {
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
  } catch (e) {
    return null;
  }
}

function writeSession(session) {
  try {
    if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else localStorage.removeItem(SESSION_KEY);
  } catch (e) {
    console.warn('[demo] 会话写入失败', e);
  }
}

function currentUsername() {
  const session = readSession();
  return session && session.username ? session.username : null;
}

function requireSession() {
  const username = currentUsername();
  if (!username) throw new Error('请先登录');
  return username;
}

function buildUser(username) {
  return { id: username, username };
}

/** 本机已存在的档案名，登录页用它向访问者展示「数据互相隔离」确有其事 */
export function listAccountNames() {
  return Object.keys(readAccounts());
}

/* ------------------------------------------------------------------ *
 * 药品参考库 + 规则校验 + 文本解析
 *
 * 演示版没有后端，校验和解析都得在浏览器里跑，所以这里有一份与服务端
 * server/src/services/aiService.js 对齐的实现。
 * 药品库本体放在 drugDatabase.js，由服务端那份自动转换而来（不手抄），
 * 保证两版的数据不会各自漂移。
 *
 * 口径：这是正则 + 人工整理的参考库，**不是 AI 模型**。
 * 界面上原来叫「AI 校验」，已改口径，别写回去。
 * ------------------------------------------------------------------ */

const MASS_UNIT_TO_MG = {
  '毫克': 1, mg: 1,
  '微克': 0.001, μg: 0.001, ug: 0.001,
  g: 1000, '克': 1000,
};
const DOSE_FORM_UNITS = ['片', '粒', '袋', '滴', '丸', '支', '喷', '单位'];
// 定 20 而不是 10：中成药本来就有一次 10 丸的（复方丹参滴丸），定太小会天天误报
const DOSE_FORM_SANE_LIMIT = 20;
const MASS_SANE_LIMIT_MG = 3000;
const UNIT_ALIAS = { mg: '毫克', ml: '毫升', g: '克', μg: '微克', ug: '微克' };
const UNIT_PATTERN = '毫克|微克|毫升|μg|ug|mg|ml|g|克|片|粒|袋|滴|丸|支|喷';

const normalizeUnit = (unit) => {
  if (!unit) return '';
  const u = String(unit).trim();
  return UNIT_ALIAS[u.toLowerCase()] || u;
};

/** 拆出剂量数值与单位，兼容 '100mg' 与 ('100','毫克') 两种入参 */
const parseDose = (dosage, unit) => {
  const text = String(dosage === undefined || dosage === null ? '' : dosage).trim();
  if (!text) return { num: null, unit: normalizeUnit(unit) };
  const matched = text.match(/^([\d.]+)\s*(.*)$/);
  if (!matched) return { num: null, unit: normalizeUnit(unit) };
  const num = Number(matched[1].replace(/\.$/, ''));
  if (isNaN(num) || num <= 0) return { num: null, unit: normalizeUnit(unit) };
  const inlineUnit = (matched[2] || '').trim();
  return { num, unit: normalizeUnit(inlineUnit || unit) };
};

/** 把参考区间拼成「2.5-10mg」这样的人话 */
const rangeText = (range, unit) => `${range[0]}-${range[1]}${unit}`;

/**
 * 校验药品信息。分级原则与服务端保持一致：
 *   error       —— 剂量超出安全范围（超上限 3 倍），必须拦住
 *   warning     —— 剂量或频次偏离常见范围，提醒确认，但可以继续
 *   suggestions —— 信息型提示。**「这个药没收录」属于这一类**：
 *                  原来把它当警告，等于对绝大多数药都在报警，
 *                  用户被刷屏之后就不看这个提示了。
 */
function runValidate(data) {
  const { name, dosage, unit, frequency, method } = data || {};
  const warnings = [];
  const errors = [];
  const suggestions = [];

  const hit = matchKnownDrug(name);
  const drugInfo = hit ? hit.info : null;
  const { num, unit: doseUnit } = parseDose(dosage, unit);
  const isMassUnit = MASS_UNIT_TO_MG[doseUnit] !== undefined;
  const isDoseFormUnit = DOSE_FORM_UNITS.includes(doseUnit);
  const range = drugInfo && drugInfo.dosageRange ? drugInfo.dosageRange : null;

  if (!drugInfo) {
    suggestions.push(
      `「${name}」暂未收录进常用药参考库，无法自动比对它的常规剂量范围，请以药盒说明书为准`
    );
  }

  if (num !== null) {
    if (range && isMassUnit) {
      // 质量单位：统一换算成 mg 再比
      const mg = num * MASS_UNIT_TO_MG[doseUnit];
      const shown = `${num}${doseUnit}`;
      if (mg > range[1] * 3) {
        errors.push(`「${name}」常用剂量范围为 ${rangeText(range, drugInfo.unit)}，您录入的 ${shown}（约 ${mg}mg）远超上限，请务必核对说明书原件！`);
      } else if (mg > range[1]) {
        warnings.push(`「${name}」常用剂量范围为 ${rangeText(range, drugInfo.unit)}，您录入的 ${shown}（约 ${mg}mg）略高于上限，请确认`);
      } else if (mg < range[0] / 2) {
        warnings.push(`「${name}」常用剂量范围为 ${rangeText(range, drugInfo.unit)}，您录入的 ${shown}（约 ${mg}mg）低于常用范围，请确认`);
      }
    } else if (range && isDoseFormUnit && drugInfo.unit === doseUnit) {
      // 剂型单位（片/粒/袋/丸）且与参考库单位一致：直接比数量
      if (num > range[1] * 3) {
        errors.push(`「${name}」常用剂量为每次 ${rangeText(range, doseUnit)}，您录入的 ${num}${doseUnit} 远超上限，请核对处方`);
      } else if (num > range[1]) {
        warnings.push(`「${name}」常用剂量为每次 ${rangeText(range, doseUnit)}，您录入的 ${num}${doseUnit} 偏多，请确认`);
      }
    } else if (isDoseFormUnit) {
      // 单位对不上（库里按 mg、用户按「片」），或库里本就没剂量区间：
      // 只抓明显离谱的数量，不做区间比对 —— 单位不同的比较没有意义
      if (num > DOSE_FORM_SANE_LIMIT) {
        warnings.push(`单次录入 ${num}${doseUnit}，剂量数值偏大，请核对是否填错`);
      }
    } else if (isMassUnit) {
      // 库外药 + 质量单位：按「单次 3g」这条粗线兜底
      const mg = num * MASS_UNIT_TO_MG[doseUnit];
      if (mg > MASS_SANE_LIMIT_MG) {
        warnings.push(`单次 ${num}${doseUnit}（约 ${mg}mg）剂量数值偏大，请核对是否填错`);
      }
    } else if (!doseUnit) {
      suggestions.push('建议补充剂量单位（毫克 / 片 / 粒），便于核对与语音播报');
    }
  }

  if (drugInfo && frequency && drugInfo.commonFreq && !drugInfo.commonFreq.includes(frequency)) {
    warnings.push(`「${name}」常用服用频次为 ${drugInfo.commonFreq.join(' 或 ')}，您选择的「${frequency}」较少见，请确认`);
  }

  if (!method) {
    suggestions.push('建议补充服用方式（饭前 / 饭后 / 空腹 / 睡前），提醒时可一并播报');
  }

  let level = 'ok';
  if (errors.length > 0) level = 'error';
  else if (warnings.length > 0) level = 'warning';

  return {
    level,
    valid: level === 'ok',
    warnings,
    errors,
    suggestions,
    drugInfo: drugInfo
      ? { name, dosageRange: drugInfo.dosageRange, unit: drugInfo.unit, inLibrary: true }
      : { name, dosageRange: null, unit: null, inLibrary: false },
  };
}

/* ------------------------------------------------------------------ *
 * 文本解析（拍照 OCR 与语音/手输文本共用）
 * ------------------------------------------------------------------ */

const CN_NUM = { '一': 1, '两': 2, '二': 2, '三': 3, '四': 4, '五': 5, '六': 6, '七': 7, '八': 8, '九': 9 };
const DEFAULT_TIMES = {
  1: ['08:00'],
  2: ['08:00', '18:00'],
  3: ['08:00', '12:00', '18:00'],
  4: ['08:00', '12:00', '18:00', '21:00'],
};

/** 中文数字转阿拉伯数字，支持 半 / 一~九 / 十 / 十二 / 二十 / 二十五 */
function cnToNumber(str) {
  if (!str) return null;
  if (/^\d+(\.\d+)?$/.test(str)) return Number(str);
  if (str === '半') return 0.5;
  if (str === '十') return 10;
  if (str.startsWith('十')) return 10 + (CN_NUM[str[1]] || 0);
  if (str.endsWith('十')) return (CN_NUM[str[0]] || 0) * 10;
  if (str.includes('十')) {
    const [a, b] = str.split('十');
    return (CN_NUM[a] || 0) * 10 + (CN_NUM[b] || 0);
  }
  if (str.length === 1) return CN_NUM[str] !== undefined ? CN_NUM[str] : null;
  return null;
}

/**
 * 中文口语里，剂量数字前面可能出现的「引导词」。
 *
 * 列全是关键：老人不会说「每次一片」这种标准句式，说的是
 * 「每天早上一片」「每晚一片」「一天三支」「饭后一片」——
 * 少列一个词，那句话就会变成「频次认出来了、剂量却是空的」，
 * 用户看到的就是「表格自己填了一半」。
 * 按长度降序排列，让「每天」优先于「每」被匹配。
 */
const DOSE_LEAD =
  '每次|一次|每顿|每天|每日|一天|一日|早上|早晨|清晨|上午|中午|下午|晚上|晚间|傍晚' +
  '|睡前|每晚|饭后|餐后|饭前|餐前|空腹|服用|吃|服|喝|用|餐|每|各|约';

/** 引导词后面还可能再跟一个「时段」词：「每天 + 早上 + 一片」 */
const TIME_OF_DAY = '早上|早晨|清晨|上午|中午|下午|晚上|晚间|傍晚|睡前';

/**
 * 从文本抽取药品信息。
 * 返回的 dosage 一定是纯数字字符串、单位单独放在 unit —— 表单的剂量输入框是 number 类型，
 * 把 "100mg" 塞进去会让提交校验必然失败。
 */
function parseDrugText(text) {
  const result = {
    name: '', dosage: '', unit: '', frequency: '', times: [],
    method: '', notes: '', rawText: text || '',
  };
  if (!text) return result;

  const compact = String(text).replace(/\s+/g, '');

  // —— 药品名称 ——
  // 先查参考库；库里没有的，按剂型后缀从原话里抠出来。
  // 这一步是「语音说了药名却填不进去」的直接修复点：
  // 原来只认参考库里的药，库外一概识别不出名字。
  const found = extractDrugName(compact);
  const drugInfo = found && found.info ? found.info : null;
  if (found) result.name = found.name;

  // —— 剂量：优先阿拉伯数字，其次中文数字 ——
  const arabic = compact.match(new RegExp(`(\\d+(?:\\.\\d+)?)(${UNIT_PATTERN})`, 'i'));
  if (arabic) {
    result.dosage = arabic[1];
    result.unit = normalizeUnit(arabic[2]);
  } else {
    // 引导词要列全（见 DOSE_LEAD 注释）：「早晚各一袋」「每天早上一片」「每晚一片」
    // 「一天三支」这类说法在口语里很常见，少一个词就会出现「剂量却是空的」
    const chinese = compact.match(
      new RegExp(
        `(?:${DOSE_LEAD})\\s*(?:${TIME_OF_DAY})?\\s*(半|[一二两三四五六七八九十]+)\\s*(${UNIT_PATTERN})`,
        'i'
      )
    );
    if (chinese) {
      const n = cnToNumber(chinese[1]);
      if (n !== null) {
        result.dosage = String(n);
        result.unit = normalizeUnit(chinese[2]);
      }
    }
  }
  // 只识别出数值没识别出单位时，用参考库的默认单位兜底
  if (result.dosage && !result.unit && drugInfo && drugInfo.unit) {
    result.unit = normalizeUnit(drugInfo.unit);
  }

  // —— 服用频次 ——
  // 中间的动词不能少：「一天吃两次」是口语里最常见的说法；
  // 正则里不放过「吃 / 服 / 喝 / 用 / 要」，它就会被漏掉，最后兜底成「每日1次」——
  // 频次被低估意味着**提醒少响**，比多响危险得多。
  const freqMatch = compact.match(
    /(?:每天|每日|一日|一天|天天|1日)\s*(?:要|得|吃|服|服用|喝|用|含)?\s*([0-9一二两三四五六七八九十]+)\s*次/
  );
  if (freqMatch) {
    const n = cnToNumber(freqMatch[1]);
    if (n && DEFAULT_TIMES[n]) {
      result.frequency = `每日${n}次`;
      result.times = DEFAULT_TIMES[n].slice();
    }
  }
  if (!result.frequency && /早晚/.test(compact)) {
    result.frequency = '每日2次';
    result.times = DEFAULT_TIMES[2].slice();
  }
  // 「早上一次、晚上一次」——把两顿分开说、又没用「一天两次」总括的说法。
  // 收紧成「早上的词 → 六个字以内 → 晚上的词」，中间夹了逗号就不算，
  // 免得把「早上吃的药，晚上忘了」这种叙述也当成一天两次。
  if (!result.frequency
      && /(早上|早晨|清晨|上午)[^，。；]{0,6}(晚上|晚间|傍晚|睡前|每晚)/.test(compact)) {
    result.frequency = '每日2次';
    result.times = DEFAULT_TIMES[2].slice();
  }
  if (!result.frequency && /(每周|一周)\s*([0-9一]+)\s*次/.test(compact)) result.frequency = '每周1次';
  if (!result.frequency && /隔日|隔天/.test(compact)) result.frequency = '隔日1次';

  // —— 显式时间点优先于频次推导：「晚上8点」-> 20:00 ——
  const hourMatch = compact.match(/(早上|早晨|上午|中午|下午|晚上|晚间|傍晚)?(\d{1,2})点/);
  if (hourMatch) {
    let hour = parseInt(hourMatch[2], 10);
    const period = hourMatch[1] || '';
    if (/下午|晚上|晚间|傍晚/.test(period) && hour < 12) hour += 12;
    if (/中午/.test(period) && hour < 12) hour = 12;
    if (hour >= 0 && hour <= 23) result.times = [`${pad(hour)}:00`];
  }

  // —— 服用方式 ——
  if (/饭后|餐后|吃完饭/.test(compact)) result.method = '饭后';
  else if (/饭前|餐前|吃饭前/.test(compact)) result.method = '饭前';
  else if (/空腹|饿着/.test(compact)) result.method = '空腹';
  else if (/睡前|睡觉前/.test(compact)) result.method = '睡前';
  if (result.method) result.notes = `${result.method}服用`;

  // —— 没点明频次、只说了时段时按时段推断 ——
  if (result.times.length === 0) {
    if (/早上|早晨|上午|早起/.test(compact)) result.times = ['08:00'];
    else if (/中午|午间|午饭|午餐/.test(compact)) result.times = ['12:00'];
    else if (/睡前|睡觉前|每晚/.test(compact)) result.times = ['21:00'];
    else if (/晚上|晚间|傍晚|下午|晚饭|晚餐/.test(compact)) result.times = ['18:00'];
  }
  if (result.times.length === 0) result.times = ['08:00'];

  if (!result.frequency && /每天|每日|一天|长期|坚持|常规/.test(compact)) {
    result.frequency = '每日1次';
  }

  return result;
}

/* ------------------------------------------------------------------ *
 * 数据持久化
 * ------------------------------------------------------------------ */

const DEFAULT_SETTINGS = {
  app_name: '小药盒',
  // 语音拆成两个开关：announce=到点播报药名剂量，ui=界面操作反馈
  voice_announce: 'true',
  voice_ui: 'true',
  voice_speed: '1',
  theme_color: '#FF8C42',
  dark_mode: 'false',
  font_size: '18',
  emergency_contact_1: '',
  emergency_contact_2: '',
  user_name: '',
  reminder_sound: 'true',
  reminder_repeat: 'true',
  reminder_vibrate: 'true',
  // 徽章「认真核对」的计数（AI 校验被调用次数）
  ai_validate_count: '0',
};

function emptyDB() {
  return {
    medicines: [],
    reminders: [],
    records: [],
    // 提醒日志：与 records（记结果）分工不同，记的是「系统这一侧发生了什么」
    reminder_logs: [],
    settings: { ...DEFAULT_SETTINGS },
    streaks: { current_streak: 0, longest_streak: 0, last_complete_date: null },
    // log 这个计数器必须在这里就给初值：不写的话 ++db.seq.log 会算出 NaN，
    // 旧档案（没有该字段）合并后同样会踩到，所以 loadDB 里也要补一次。
    seq: { medicine: 0, reminder: 0, record: 0, log: 0 },
  };
}

/**
 * 种子数据：让访问者打开即看到「有内容」的完整产品，而不是空壳。
 * 覆盖近 14 天的用药记录，制造出打卡天数、日历视图、统计图表、徽章等全部可视内容。
 * 每个新档案都会写入这份种子数据 —— 作品集场景下，一个空应用没人愿意点第二下。
 */
function buildSeed() {
  const db = emptyDB();
  db.settings.user_name = '张阿姨';
  // 让「认真核对」徽章在演示里是已获得状态（对应真实的 AI 校验调用次数）
  db.settings.ai_validate_count = '6';

  const meds = [
    { name: '苯磺酸氨氯地平片', dosage: '5', unit: '毫克', frequency: '每日1次', times: ['08:00'], method: '饭后', notes: '降压药，每日固定时间服用，不可随意停药' },
    { name: '阿司匹林肠溶片', dosage: '100', unit: '毫克', frequency: '每日1次', times: ['08:00'], method: '饭后', notes: '抗血小板，饭后整片吞服，不可嚼碎' },
    { name: '二甲双胍缓释片', dosage: '0.5', unit: '片', frequency: '每日2次', times: ['08:00', '18:00'], method: '饭后', notes: '降糖药，随餐或饭后服用' },
    { name: '阿托伐他汀钙片', dosage: '20', unit: '毫克', frequency: '每日1次', times: ['21:00'], method: '睡前', notes: '降脂药，睡前服用效果更好' },
    // 暂停状态的药品：用来展示「住院/换药期间先停掉」这个能力
    { name: '头孢呋辛酯片', dosage: '0.25', unit: '片', frequency: '每日2次', times: ['08:00', '20:00'], method: '饭后', notes: '抗生素疗程已结束，暂停提醒', status: 'paused' },
  ];

  meds.forEach((m) => {
    const id = ++db.seq.medicine;
    db.medicines.push({
      id,
      user_id: 1,
      name: m.name,
      dosage: m.dosage,
      unit: m.unit,
      frequency: m.frequency,
      times: JSON.stringify(m.times),
      method: m.method,
      notes: m.notes,
      status: m.status || 'active',
      created_at: `${fmtDate(shiftDays(-20))} 09:00:00`,
      updated_at: `${fmtDate(shiftDays(-20))} 09:00:00`,
    });
  });

  /**
   * 刻意安排的漏服，让「首页漏服提示」「日历红点」「提醒日志」三处都有真实数据可看。
   *
   * 形状：天数偏移 -> { 时间: 药品名关键字 }
   * 今天那次（0）只有在当前时间已过 08:00 时才会出现 —— 演示数据不该凭空捏造未来。
   * 刻意不放在昨天：昨天一漏，连续打卡就会归零，首页那张打卡卡就空了。
   */
  const missedPlan = {
    0: { '08:00': '二甲双胍' },
    4: { '08:00': '阿司匹林' },
    9: { '21:00': '阿托伐他汀' },
  };

  for (let d = 14; d >= 0; d--) {
    const date = fmtDate(shiftDays(-d));
    const isToday = d === 0;
    const curTime = nowTimeStr();
    const plan = missedPlan[d] || {};

    for (const med of db.medicines) {
      if (med.status === 'paused') continue;
      const times = JSON.parse(med.times);
      for (const t of times) {
        const remId = ++db.seq.reminder;

        // 今天只生成「当前时刻及之前」的记录，之后的时间保持待服，进度条才有意义
        if (isToday && t > curTime) {
          db.reminders.push({
            id: remId, user_id: 1, medicine_id: med.id,
            reminder_time: t, status: 'pending', triggered_at: null, confirmed_at: null,
            reminder_date: date, snooze_count: 0, next_trigger_at: null, delivered_at: null,
          });
          continue;
        }

        // 只有规划里点名的那个药品在那个时间点才算漏服
        const isMissed = !!(plan[t] && med.name.includes(plan[t]));

        db.reminders.push({
          id: remId, user_id: 1, medicine_id: med.id,
          reminder_time: t,
          status: isMissed ? 'missed' : 'confirmed',
          triggered_at: `${date} ${t}:00`,
          confirmed_at: isMissed ? null : `${date} ${t}:03`,
          reminder_date: date, snooze_count: 0, next_trigger_at: null,
          delivered_at: `${date} ${t}:01`,
        });

        db.records.push({
          id: ++db.seq.record, user_id: 1, medicine_id: med.id,
          medicine_name: med.name, dosage: med.dosage, unit: med.unit,
          scheduled_time: t,
          actual_time: isMissed ? null : `${t.slice(0, 2)}:0${Math.min(9, parseInt(t.slice(3), 10) + 3)}`,
          status: isMissed ? 'missed' : 'taken',
          record_date: date,
        });

        // 日志：把「触发 → 送达 → 结果」这条链完整写出来，日志页才有过程可看
        const logBase = { reminderId: remId, medicineId: med.id, medicineName: med.name, scheduledTime: t, logDate: date };
        logEvent(db, { ...logBase, event: 'triggered', detail: '到点触发', logTime: `${t}:00` });
        logEvent(db, { ...logBase, event: 'delivered', detail: '提醒弹窗已在设备上显示', logTime: `${t}:01` });
        if (isMissed) {
          logEvent(db, { ...logBase, event: 'missed', detail: `提醒已发出，${MISS_GRACE_MINUTES} 分钟内未确认`, logTime: `${t}:16` });
        } else {
          logEvent(db, {
            ...logBase, event: 'confirmed',
            detail: `实际服用时间 ${t.slice(0, 2)}:0${Math.min(9, parseInt(t.slice(3), 10) + 3)}`,
            logTime: `${t}:03`,
          });
        }
      }
    }
  }

  // 连续打卡：从昨天往前数，遇到有漏服的日子就断
  let streak = 0;
  for (let d = 1; d <= 14; d++) {
    const date = fmtDate(shiftDays(-d));
    const dayRecords = db.records.filter(r => r.record_date === date);
    if (dayRecords.length === 0) break;
    if (dayRecords.some(r => r.status === 'missed')) break;
    streak++;
  }
  db.streaks = {
    current_streak: streak,
    longest_streak: Math.max(streak, 4),
    last_complete_date: fmtDate(shiftDays(-1)),
  };

  return db;
}

/** 当前账号的数据缓存；cacheOwner 用于防止切换账号后读到上一个人的数据 */
let cache = null;
let cacheOwner = null;

function loadDB() {
  const username = requireSession();
  if (cache && cacheOwner === username) return cache;
  try {
    const raw = localStorage.getItem(dbKey(username));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && Array.isArray(parsed.medicines)) {
        // seq 必须逐字段合并：旧档案里没有 log 这个计数器，
        // 整体覆盖会让 ++db.seq.log 变成 NaN，新写入的日志 id 全是 NaN。
        const base = emptyDB();
        cache = {
          ...base,
          ...parsed,
          reminder_logs: Array.isArray(parsed.reminder_logs) ? parsed.reminder_logs : [],
          settings: { ...DEFAULT_SETTINGS, ...(parsed.settings || {}) },
          seq: { ...base.seq, ...(parsed.seq || {}) },
        };
        cacheOwner = username;
        return cache;
      }
    }
  } catch (e) {
    console.warn('[demo] 本地数据解析失败，已重建演示数据', e);
  }
  cache = buildSeed();
  cacheOwner = username;
  persist();
  return cache;
}

function persist() {
  if (!cacheOwner) return;
  try {
    localStorage.setItem(dbKey(cacheOwner), JSON.stringify(cache));
  } catch (e) {
    console.warn('[demo] 本地存储写入失败', e);
  }
}

/** 供界面「重置演示数据」调用：只清空当前账号的数据，不影响本机其他档案 */
export function resetDemoData() {
  const username = currentUsername();
  if (!username) return null;
  cache = buildSeed();
  cacheOwner = username;
  persist();
  return cache;
}

/* ------------------------------------------------------------------ *
 * 提醒调度（平移自 server/src/services/reminderScheduler.js）
 * ------------------------------------------------------------------ */

function generateDailyReminders(db) {
  const today = todayStr();
  const curTime = nowTimeStr();
  for (const med of db.medicines.filter(m => m.status === 'active')) {
    let times = [];
    try { times = JSON.parse(med.times); } catch (e) { times = []; }
    if (!Array.isArray(times)) times = [];
    for (const t of times) {
      if (typeof t !== 'string' || !/^\d{2}:\d{2}$/.test(t)) continue;
      // 不补生成「今天已经过去」的时间点：新增、编辑、恢复暂停药品时，
      // 倒推一条漏服出来是凭空定罪（与后端口径一致）
      if (t < curTime) continue;
      const exists = db.reminders.some(
        r => r.medicine_id === med.id && r.reminder_date === today && r.reminder_time === t
      );
      if (!exists) {
        db.reminders.push({
          id: ++db.seq.reminder, user_id: 1, medicine_id: med.id,
          reminder_time: t, status: 'pending', triggered_at: null, confirmed_at: null,
          reminder_date: today, snooze_count: 0, next_trigger_at: null, delivered_at: null,
        });
      }
    }
  }
}

function decorateReminder(db, r) {
  const med = db.medicines.find(m => m.id === r.medicine_id) || {};
  return {
    ...r,
    medicine_name: med.name || '未知药品',
    dosage: med.dosage || '',
    unit: med.unit || '片',
    method: med.method || '',
    notes: med.notes || '',
    medicine_status: med.status || 'active',
  };
}

/** 一条提醒「实际该响的时间」：顺延过就用顺延后的 */
const effectiveTime = r => (r.next_trigger_at ? r.next_trigger_at.slice(11, 16) : r.reminder_time);

/** 漏服：改状态 + 写用药记录 + 写日志，三件事必须一起做 */
function markMissed(db, r, med, reason) {
  r.status = 'missed';
  const dup = db.records.some(
    x => x.medicine_id === r.medicine_id && x.record_date === r.reminder_date
      && x.scheduled_time === r.reminder_time && x.status === 'missed'
  );
  if (!dup) {
    db.records.push({
      id: ++db.seq.record, user_id: 1, medicine_id: r.medicine_id,
      medicine_name: med.name, dosage: med.dosage, unit: med.unit,
      scheduled_time: r.reminder_time, actual_time: null,
      status: 'missed', record_date: r.reminder_date,
    });
  }
  logEvent(db, {
    reminderId: r.id, medicineId: r.medicine_id, medicineName: med.name,
    scheduledTime: r.reminder_time, event: 'missed', detail: reason,
  });
}

/**
 * 推进提醒状态，返回「需要立刻弹给用户看」的提醒。
 *
 * 含两类：这一分钟刚到点的，以及「已触发但设备还没收到」的（页面迟到打开时的补偿）。
 */
function checkAndTrigger(db) {
  const today = todayStr();
  const now = nowTimeStr();
  const nowMs = Date.now();

  // 1) 判漏服（严格小于，不能吃掉本该这一分钟触发的提醒）
  for (const r of db.reminders) {
    if (r.reminder_date !== today) continue;
    const med = db.medicines.find(m => m.id === r.medicine_id);
    if (!med) continue;
    if (r.status === 'pending' && effectiveTime(r) < now) {
      markMissed(db, r, med, r.next_trigger_at ? '顺延时间过后仍未确认' : '到点后一直未确认');
    } else if (r.status === 'triggered' && r.triggered_at) {
      const tMs = new Date(r.triggered_at.replace(/-/g, '/')).getTime();
      if (nowMs - tMs > MISS_GRACE_MINUTES * 60 * 1000) {
        markMissed(db, r, med, `提醒已发出，${MISS_GRACE_MINUTES} 分钟内未确认`);
      }
    }
  }

  // 2) 到点的改成已触发
  const due = db.reminders.filter(r => {
    if (r.reminder_date !== today || r.status !== 'pending') return false;
    const med = db.medicines.find(m => m.id === r.medicine_id);
    return med && med.status === 'active' && effectiveTime(r) === now;
  });

  for (const r of due) {
    r.status = 'triggered';
    r.triggered_at = stamp();
    // 必须清掉送达标记：顺延后会重新触发，留着它就永远不会再弹
    r.delivered_at = null;
    const med = db.medicines.find(m => m.id === r.medicine_id) || {};
    logEvent(db, {
      reminderId: r.id, medicineId: r.medicine_id, medicineName: med.name,
      scheduledTime: r.reminder_time, event: 'triggered',
      detail: r.next_trigger_at ? `顺延后到点（原定 ${r.reminder_time}）` : '到点触发',
    });
  }

  // 3) 取所有「已触发但还没送到设备」的交给前端
  const toDeliver = db.reminders
    .filter(r => {
      if (r.reminder_date !== today || r.status !== 'triggered' || r.delivered_at) return false;
      const med = db.medicines.find(m => m.id === r.medicine_id);
      return med && med.status === 'active';
    })
    .sort((a, b) => effectiveTime(a).localeCompare(effectiveTime(b)));

  persist();
  return toDeliver.map(r => decorateReminder(db, r));
}

/** 弹窗真的显示了之后回写 */
function markDelivered(db, id) {
  const r = db.reminders.find(x => String(x.id) === String(id));
  if (!r || r.delivered_at) return false;
  r.delivered_at = stamp();
  const med = db.medicines.find(m => m.id === r.medicine_id) || {};
  logEvent(db, {
    reminderId: r.id, medicineId: r.medicine_id, medicineName: med.name,
    scheduledTime: r.reminder_time, event: 'delivered', detail: '提醒弹窗已在设备上显示',
  });
  persist();
  return true;
}

/**
 * 结算某一天的打卡。
 *
 * 关键点：确认服药时立刻结算「那一天」，而不是等午夜定时任务。
 * 原实现只在午夜结算，那时「今天」的提醒大多还是 pending，判断必然失败 ——
 * 打卡天数会永远是 0。dateStr 默认今天；午夜兜底任务传入「昨天」。
 */
function updateStreak(db, dateStr) {
  const date = dateStr || todayStr();
  const dayReminders = db.reminders.filter(r => r.reminder_date === date);
  if (dayReminders.length === 0) return;
  // skipped 也算完成：老人主动跳过，不该把连续打卡判死
  const incomplete = dayReminders.filter(r => !['confirmed', 'skipped'].includes(r.status));
  if (incomplete.length > 0) return;
  if (db.streaks.last_complete_date === date) return;

  // 「昨天」要用被结算的那天倒推，不能用 now —— 午夜补结算时 now 已经是新的一天了
  const prev = new Date(`${date}T12:00:00`);
  prev.setDate(prev.getDate() - 1);
  const prevStr = fmtDate(prev);
  const newCurrent = db.streaks.last_complete_date === prevStr ? db.streaks.current_streak + 1 : 1;
  db.streaks = {
    current_streak: newCurrent,
    longest_streak: Math.max(newCurrent, db.streaks.longest_streak || 0),
    last_complete_date: date,
  };
}

/* ------------------------------------------------------------------ *
 * 徽章
 * ------------------------------------------------------------------ */

const BADGES = [
  { id: 1, name: '初次服药', icon: '🌱', description: '第一次确认服药', condition: { type: 'total_taken', value: 1 } },
  { id: 2, name: '坚持三天', icon: '🔥', description: '连续3天按时服药', condition: { type: 'streak', value: 3 } },
  { id: 3, name: '坚持七天', icon: '⭐', description: '连续7天按时服药', condition: { type: 'streak', value: 7 } },
  { id: 4, name: '坚持半月', icon: '🏆', description: '连续15天按时服药', condition: { type: 'streak', value: 15 } },
  { id: 5, name: '坚持一月', icon: '👑', description: '连续30天按时服药', condition: { type: 'streak', value: 30 } },
  { id: 6, name: '服药十次', icon: '💊', description: '累计服药10次', condition: { type: 'total_taken', value: 10 } },
  { id: 7, name: '服药百次', icon: '💯', description: '累计服药100次', condition: { type: 'total_taken', value: 100 } },
  { id: 8, name: '认真核对', icon: '🔍', description: '核对药品信息 5 次', condition: { type: 'ai_validate', value: 5 } },
  { id: 9, name: '健康管理', icon: '🌟', description: '累计服药500次', condition: { type: 'total_taken', value: 500 } },
];

function getBadges(db) {
  const totalTaken = db.records.filter(r => r.status === 'taken').length;
  // 「认真核对」看的是 AI 校验被调用次数，存在 settings 里（与后端 badgeService 一致）
  const aiCount = Number(db.settings.ai_validate_count || 0);
  return BADGES.map(b => {
    let earned = false;
    if (b.condition.type === 'streak') earned = (db.streaks.current_streak || 0) >= b.condition.value;
    else if (b.condition.type === 'total_taken') earned = totalTaken >= b.condition.value;
    else if (b.condition.type === 'ai_validate') earned = aiCount >= b.condition.value;
    return { ...b, earned };
  });
}

/* ------------------------------------------------------------------ *
 * OCR：浏览器端 tesseract.js（懒加载，主备 CDN，失败给出可操作提示）
 * ------------------------------------------------------------------ */

const TESSERACT_CDNS = [
  'https://cdn.jsdelivr.net/npm/tesseract.js@5.1.1/dist/tesseract.min.js',
  'https://unpkg.com/tesseract.js@5.1.1/dist/tesseract.min.js',
];

let tesseractLoading = null;

function loadScript(src, timeoutMs) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      script.remove();
      reject(new Error('加载超时'));
    }, timeoutMs);
    script.src = src;
    script.onload = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve();
    };
    script.onerror = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      script.remove();
      reject(new Error('加载失败'));
    };
    document.head.appendChild(script);
  });
}

function loadTesseract() {
  if (window.Tesseract) return Promise.resolve(window.Tesseract);
  if (tesseractLoading) return tesseractLoading;

  tesseractLoading = (async () => {
    let lastErr = null;
    for (const url of TESSERACT_CDNS) {
      try {
        await loadScript(url, 25000);
        if (window.Tesseract) return window.Tesseract;
      } catch (e) {
        lastErr = e;
      }
    }
    throw lastErr || new Error('识别组件不可用');
  })().catch(err => {
    // 失败后允许用户重试，不要永久缓存这次失败
    tesseractLoading = null;
    throw err;
  });

  return tesseractLoading;
}

async function recognizeImage(base64) {
  let Tesseract;
  try {
    Tesseract = await loadTesseract();
  } catch (e) {
    throw new Error('浏览器内置识别组件加载失败（首次使用需联网下载约 10MB 中文识别模型）。请检查网络后重试，或直接用手动录入');
  }
  try {
    // 中文识别模型同样需要联网下载，第一次会比较慢
    const { data } = await Tesseract.recognize(base64, 'chi_sim');
    return (data && data.text) ? data.text : '';
  } catch (e) {
    throw new Error('图片识别失败：' + (e.message || '请重试'));
  }
}

/* ------------------------------------------------------------------ *
 * 对外 API（方法名与返回结构对齐原 REST 接口）
 * ------------------------------------------------------------------ */

export const localApi = {
  /* ---------- 认证：本机档案，每个账号一份独立数据 ---------- */
  async register({ username, password } = {}) {
    const name = String(username || '').trim();
    if (name.length < 2) throw new Error('用户名至少2位');
    if (String(password || '').length < 4) throw new Error('密码至少4位');

    const accounts = readAccounts();
    if (accounts[name]) throw new Error('该用户名已存在，请直接登录或换一个');

    accounts[name] = {
      username: name,
      passwordHash: hashPassword(name, password),
      createdAt: new Date().toISOString(),
    };
    writeAccounts(accounts);

    // 新档案同样写入演示数据：作品集场景下空壳页面等于劝退
    cache = buildSeed();
    cacheOwner = name;
    persist();
    writeSession({ username: name, loginAt: Date.now() });

    return { token: `demo-token:${name}`, user: buildUser(name) };
  },

  async login({ username, password } = {}) {
    const name = String(username || '').trim();
    const accounts = readAccounts();
    const account = accounts[name];
    if (!account) throw new Error('账号不存在，请先注册');
    if (account.passwordHash !== hashPassword(name, password)) throw new Error('密码不正确');

    // 切换账号时清掉缓存，避免读到上一个账号的数据
    cache = null;
    cacheOwner = null;
    writeSession({ username: name, loginAt: Date.now() });

    return { token: `demo-token:${name}`, user: buildUser(name) };
  },

  async logout() {
    writeSession(null);
    cache = null;
    cacheOwner = null;
    return { message: '已退出登录' };
  },

  async getMe() {
    const username = currentUsername();
    if (!username) throw new Error('未登录');
    if (!readAccounts()[username]) {
      writeSession(null);
      throw new Error('账号不存在');
    }
    return { user: buildUser(username) };
  },

  async changePassword({ oldPassword, newPassword } = {}) {
    const username = requireSession();
    const accounts = readAccounts();
    const account = accounts[username];
    if (!account) throw new Error('账号不存在');
    if (account.passwordHash !== hashPassword(username, oldPassword)) throw new Error('原密码不正确');
    if (String(newPassword || '').length < 4) throw new Error('新密码至少4位');

    account.passwordHash = hashPassword(username, newPassword);
    writeAccounts(accounts);
    return { message: '密码修改成功' };
  },

  /* ---------- 药品 ---------- */
  async getMedicines() {
    const db = loadDB();
    return clone(db.medicines).sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  },

  async getMedicine(id) {
    const db = loadDB();
    const med = db.medicines.find(m => String(m.id) === String(id));
    if (!med) throw new Error('药品不存在');
    return clone(med);
  },

  async createMedicine(data) {
    const db = loadDB();
    if (!data.name || !data.dosage || !data.frequency) throw new Error('请填写药品名称、剂量和频次');
    const now = `${todayStr()} ${nowTimeStr()}:00`;
    const med = {
      id: ++db.seq.medicine, user_id: 1,
      name: data.name, dosage: String(data.dosage), unit: data.unit || '片',
      frequency: data.frequency,
      times: JSON.stringify(Array.isArray(data.times) ? data.times : ['08:00']),
      method: data.method || '', notes: data.notes || '',
      status: 'active', created_at: now, updated_at: now,
    };
    db.medicines.push(med);
    generateDailyReminders(db);
    persist();
    return clone(med);
  },

  async updateMedicine(id, data) {
    const db = loadDB();
    const med = db.medicines.find(m => String(m.id) === String(id));
    if (!med) throw new Error('药品不存在');
    Object.assign(med, {
      name: data.name || med.name,
      dosage: data.dosage !== undefined ? String(data.dosage) : med.dosage,
      unit: data.unit || med.unit,
      frequency: data.frequency || med.frequency,
      times: Array.isArray(data.times) ? JSON.stringify(data.times) : med.times,
      method: data.method !== undefined ? data.method : med.method,
      notes: data.notes !== undefined ? data.notes : med.notes,
      updated_at: `${todayStr()} ${nowTimeStr()}:00`,
    });
    // 与后端一致：药品编辑后清掉旧提醒，按新时间重新生成
    db.reminders = db.reminders.filter(r => r.medicine_id !== med.id);
    generateDailyReminders(db);
    persist();
    return clone(med);
  },

  async deleteMedicine(id) {
    const db = loadDB();
    db.medicines = db.medicines.filter(m => String(m.id) !== String(id));
    db.reminders = db.reminders.filter(r => String(r.medicine_id) !== String(id));
    persist();
    return { message: '药品已删除，用药记录已保留' };
  },

  async clearAllData() {
    const db = loadDB();
    db.medicines = []; db.reminders = []; db.records = []; db.reminder_logs = [];
    db.streaks = { current_streak: 0, longest_streak: 0, last_complete_date: null };
    persist();
    return { message: '所有数据已清除' };
  },

  async updateMedicineStatus(id, status) {
    const db = loadDB();
    if (!['active', 'paused'].includes(status)) throw new Error('状态值无效，请选择「启用」或「暂停」');
    const med = db.medicines.find(m => String(m.id) === String(id));
    if (!med) throw new Error('药品不存在');
    med.status = status;
    med.updated_at = stamp();

    if (status === 'paused') {
      /**
       * 暂停 = 今天剩下的这几次不要再响。
       * 只清「未完成」的提醒（pending / triggered），已确认、已跳过、已漏服的一律保留 ——
       * 那些是"真的发生过"的事实，不该因为暂停而消失。
       * generateDailyReminders 只处理 active 的药品，所以清掉之后不会再生成。
       */
      db.reminders = db.reminders.filter(
        r => !(r.medicine_id === med.id && ['pending', 'triggered'].includes(r.status))
      );
    } else {
      // 恢复：按「不补生成已过去时间点」的规则重新生成，避免凭空多出一次漏服
      generateDailyReminders(db);
    }

    persist();
    return clone(med);
  },

  /* ---------- 提醒 ---------- */
  async checkReminders() {
    const db = loadDB();
    generateDailyReminders(db);
    // 午夜兜底：昨天若已全部完成、当时却没被结算（页面当时没开着），这里补一次。
    // 演示版没有 cron，只能搭在轮询上；正式版由 index.js 的定时任务承担。
    updateStreak(db, fmtDate(shiftDays(-1)));
    const due = checkAndTrigger(db);
    return { triggered: due.length, reminders: due };
  },

  async getTodayReminders() {
    const db = loadDB();
    generateDailyReminders(db);
    persist();
    return db.reminders
      .filter(r => r.reminder_date === todayStr())
      .map(r => decorateReminder(db, r))
      .sort((a, b) => a.reminder_time.localeCompare(b.reminder_time));
  },

  async getReminders() {
    const db = loadDB();
    return db.reminders.map(r => decorateReminder(db, r));
  },

  async markReminderDelivered(id) {
    const db = loadDB();
    // 内部 markDelivered 已负责 delivered_at + 日志 + persist
    const ok = markDelivered(db, id);
    if (!ok) throw new Error('提醒不存在或已送达过');
    return { message: '已记录送达' };
  },

  async confirmReminder(id, { action }) {
    const db = loadDB();
    const r = db.reminders.find(x => String(x.id) === String(id));
    if (!r) throw new Error('提醒不存在');
    const med = db.medicines.find(m => m.id === r.medicine_id) || {};
    const medName = med.name || '未知药品';
    const today = todayStr();
    const logBase = { reminderId: r.id, medicineId: r.medicine_id, medicineName: medName, scheduledTime: r.reminder_time };

    if (action === 'taken') {
      r.status = 'confirmed';
      r.confirmed_at = stamp();
      db.records.push({
        id: ++db.seq.record, user_id: 1, medicine_id: r.medicine_id,
        medicine_name: medName, dosage: med.dosage || '', unit: med.unit || '片',
        scheduled_time: r.reminder_time, actual_time: nowTimeStr(),
        status: 'taken', record_date: today,
      });
      logEvent(db, { ...logBase, event: 'confirmed', detail: `实际服用时间 ${nowTimeStr()}` });
      updateStreak(db);
      persist();
      return { status: 'taken', record: { medicine_name: medName, status: 'taken' } };
    }

    if (action === 'snooze') {
      const count = (r.snooze_count || 0) + 1;
      if (count > MAX_SNOOZE) {
        // 用满次数：直接按漏服处理（状态+记录+日志三连，与 checkAndTrigger 同一套）
        markMissed(db, r, med, `稍后提醒已用满 ${MAX_SNOOZE} 次`);
        persist();
        return { status: 'missed', message: `已稍后提醒${MAX_SNOOZE}次，本次提醒已结束` };
      }
      const next = shiftMinutes(SNOOZE_MINUTES);
      const nextStamp = stamp(next);
      r.snooze_count = count;
      r.status = 'pending';
      r.triggered_at = null;
      r.delivered_at = null;
      /**
       * 顺延时间写 next_trigger_at，绝不改 reminder_time。
       * 改了它，generateDailyReminders 会以为「这个时间点没有提醒」而补生成一条，
       * 老人会凭空多出一次漏服。这里与服务端 reminderScheduler 口径完全一致。
       */
      r.next_trigger_at = nextStamp;
      logEvent(db, { ...logBase, event: 'snoozed', detail: `第 ${count} 次，顺延到 ${nextStamp.slice(11, 16)}` });
      persist();
      return {
        status: 'snoozed',
        snooze_count: count,
        next_trigger_at: nextStamp,
        message: `稍后提醒（第${count}次），${nextStamp.slice(11, 16)} 再叫您`,
      };
    }

    if (action === 'skip') {
      r.status = 'skipped';
      r.confirmed_at = stamp();
      db.records.push({
        id: ++db.seq.record, user_id: 1, medicine_id: r.medicine_id,
        medicine_name: medName, dosage: med.dosage || '', unit: med.unit || '片',
        scheduled_time: r.reminder_time, actual_time: null,
        status: 'skipped', record_date: today,
      });
      logEvent(db, { ...logBase, event: 'skipped', detail: '用户选择跳过本次服药' });
      updateStreak(db);
      persist();
      return { status: 'skipped', record: { medicine_name: medName, status: 'skipped' } };
    }

    throw new Error('未知操作');
  },

  /* ---------- 提醒日志：记过程，与 records（记结果）分工不同 ---------- */
  async getReminderLogs(params = '') {
    const db = loadDB();
    const q = new URLSearchParams(params.replace(/^\?/, ''));
    const date = q.get('date');
    const startDate = q.get('startDate');
    const endDate = q.get('endDate');
    const event = q.get('event');
    let list = db.reminder_logs.slice();

    if (date) {
      list = list.filter(l => l.log_date === date);
    } else {
      if (startDate) list = list.filter(l => l.log_date >= startDate);
      if (endDate) list = list.filter(l => l.log_date <= endDate);
      if (!startDate && !endDate) {
        // 默认最近 30 天，避免数据一多前端一次拉爆（与后端一致）
        const since = fmtDate(shiftDays(-30));
        list = list.filter(l => l.log_date >= since);
      }
    }
    if (event && event !== 'all') list = list.filter(l => l.event === event);

    const asc = q.get('sort') === 'time_asc';
    list.sort((a, b) => {
      const ka = `${a.log_date} ${a.log_time} ${String(a.id).padStart(8, '0')}`;
      const kb = `${b.log_date} ${b.log_time} ${String(b.id).padStart(8, '0')}`;
      return asc ? ka.localeCompare(kb) : kb.localeCompare(ka);
    });

    return clone(list).map(l => {
      const meta = LOG_EVENT_TYPES[l.event] || {};
      return { ...l, event_label: meta.label || l.event, event_icon: meta.icon || '•' };
    });
  },

  /** 有日志的日期列表，供「按天筛选」下拉用 */
  async getReminderLogDays() {
    const db = loadDB();
    const map = {};
    for (const l of db.reminder_logs) {
      if (!map[l.log_date]) map[l.log_date] = { log_date: l.log_date, count: 0, missed: 0 };
      map[l.log_date].count += 1;
      if (l.event === 'missed') map[l.log_date].missed += 1;
    }
    return Object.values(map)
      .sort((a, b) => b.log_date.localeCompare(a.log_date))
      .slice(0, 60);
  },

  /* ---------- 记录 ---------- */
  async getRecords(params = '') {
    const db = loadDB();
    const q = new URLSearchParams(params.replace(/^\?/, ''));
    let list = db.records.slice();

    const status = q.get('status');
    if (status && status !== 'all') list = list.filter(r => r.status === status);

    const startDate = q.get('startDate');
    if (startDate) list = list.filter(r => r.record_date >= startDate);

    const endDate = q.get('endDate');
    if (endDate) list = list.filter(r => r.record_date <= endDate);

    const search = q.get('search');
    if (search) list = list.filter(r => (r.medicine_name || '').includes(search));

    // 界面上的「药品」下拉用这个：精确匹配整名（跟服务端一致），
    // 不能用上面的 includes——否则选了「阿司匹林」会把「阿司匹林肠溶片」也捞进来
    const medicineName = q.get('medicineName');
    if (medicineName) list = list.filter(r => r.medicine_name === medicineName);

    const sort = q.get('sort');
    if (sort === 'date_asc') {
      list.sort((a, b) => (a.record_date + a.actual_time).localeCompare(b.record_date + b.actual_time));
    } else if (sort === 'name_asc') {
      list.sort((a, b) => a.medicine_name.localeCompare(b.medicine_name) || b.record_date.localeCompare(a.record_date));
    } else {
      list.sort((a, b) => (b.record_date + (b.actual_time || '')).localeCompare(a.record_date + (a.actual_time || '')));
    }
    // 不再合成 record_time：记录表里本来就只有 actual_time / scheduled_time，
    // 之前这里私补一个 record_time 字段，把「正式版服务端不返回这个字段、
    // 列表时间列恒为空白」的 bug 盖住了。现在前端直接读 actual_time。
    return clone(list);
  },

  async getRecordStats(params = '') {
    const db = loadDB();
    const q = new URLSearchParams(params.replace(/^\?/, ''));
    let list = db.records.slice();
    const startDate = q.get('startDate');
    const endDate = q.get('endDate');
    if (startDate) list = list.filter(r => r.record_date >= startDate);
    if (endDate) list = list.filter(r => r.record_date <= endDate);

    const result = { total: 0, taken: 0, missed: 0, skipped: 0 };
    for (const r of list) {
      result[r.status] = (result[r.status] || 0) + 1;
      result.total++;
    }
    return result;
  },

  async getRecordCalendar(year, month) {
    const db = loadDB();
    const monthStr = `${year}-${pad(month)}`;
    const list = db.records.filter(r => r.record_date.startsWith(monthStr));

    const calendarData = {};
    for (const r of list) {
      if (!calendarData[r.record_date]) {
        calendarData[r.record_date] = { total: 0, taken: 0, missed: 0, skipped: 0, items: [] };
      }
      const day = calendarData[r.record_date];
      day[r.status] = (day[r.status] || 0) + 1;
      day.total += 1;
      day.items.push({
        medicine_name: r.medicine_name, dosage: r.dosage, unit: r.unit,
        scheduled_time: r.scheduled_time, status: r.status, count: 1,
      });
    }
    for (const data of Object.values(calendarData)) {
      if (data.missed > 0) data.dotStatus = 'missed';
      else if (data.taken > 0 && data.taken === data.total) data.dotStatus = 'taken';
      else if (data.taken > 0) data.dotStatus = 'partial';
      else data.dotStatus = 'skipped';
    }
    return calendarData;
  },

  /* ---------- AI：规则校验 / OCR / 语音解析 ---------- */
  async validateMedicine(data) {
    const db = loadDB();
    const result = runValidate(data || {});
    // AI 校验被调用一次就记一次数，「认真核对」徽章靠它才有机会拿到
    db.settings.ai_validate_count = String(Number(db.settings.ai_validate_count || 0) + 1);
    // 第4道防线：重复检测。前端会把它当字符串渲染，不能 push 对象
    if (data && data.name) {
      // 除了用户原样输入的名字，也拿参考库的标准名去比一遍：
      // 输入「阿司匹林肠溶片」、已存「阿司匹林」时，靠标准名才能命中
      // （与服务端 routes/ai.js 的 LIKE 口径保持一致）
      const canonical = matchKnownDrug(data.name);
      const key = canonical ? canonical.key : data.name;
      const dup = db.medicines.filter(
        m => m.status === 'active' && (m.name.includes(data.name) || m.name.includes(key))
      );
      if (dup.length > 0) {
        result.warnings.push(
          `已有「${dup[0].name}」在药品列表中（${dup[0].dosage}${dup[0].unit}，${dup[0].frequency}），请确认是否需要重复添加`
        );
        if (result.level === 'ok') {
          result.level = 'warning';
          result.valid = false;
        }
      }
    }
    if (result.level === 'error') {
      result.suggestions.push('建议先与开药医生或药师核对剂量，确认无误后再录入');
    }
    persist();
    return result;
  },

  async ocrMedicine(imageData) {
    const rawText = await recognizeImage(imageData);
    if (!rawText || !rawText.trim()) {
      throw new Error('未能从图片中识别出文字，请对准药盒或处方单上的文字重新拍摄');
    }
    const parsed = parseDrugText(rawText);
    return {
      rawText,
      raw_text: rawText, // 兼容页面字段名
      parsed,
      message: '已识别药品标签信息，请核对以下内容是否正确',
    };
  },

  async parseVoice(text) {
    if (!text) throw new Error('请提供语音识别文本');
    const parsed = parseDrugText(text);
    return {
      rawText: text,
      raw_text: text,
      parsed,
      message: '已识别语音内容，请核对以下信息是否正确',
    };
  },

  /* ---------- 设置 ---------- */
  async getSettings() {
    return { ...loadDB().settings };
  },

  async updateSettings(data) {
    const db = loadDB();
    const allowed = [
      'app_name', 'voice_announce', 'voice_ui', 'voice_broadcast', 'voice_speed',
      'theme_color', 'dark_mode', 'font_size', 'emergency_contact_1', 'emergency_contact_2',
      'user_name', 'reminder_sound', 'reminder_repeat', 'reminder_vibrate',
    ];
    for (const [k, v] of Object.entries(data || {})) {
      if (allowed.includes(k)) db.settings[k] = String(v);
    }
    persist();
    return { ...db.settings };
  },

  /* ---------- 打卡与徽章 ---------- */
  async getStreak() {
    const db = loadDB();
    return clone(db.streaks);
  },

  async getBadges() {
    return getBadges(loadDB());
  },
};

/* ------------------------------------------------------------------ *
 * 初始化：确保演示账号存在，且预置了可看的种子数据
 * ------------------------------------------------------------------ */

function ensureDemoAccount() {
  const accounts = readAccounts();
  if (!accounts[DEMO_CREDENTIALS.username]) {
    accounts[DEMO_CREDENTIALS.username] = {
      username: DEMO_CREDENTIALS.username,
      passwordHash: hashPassword(DEMO_CREDENTIALS.username, DEMO_CREDENTIALS.password),
      createdAt: new Date().toISOString(),
      isDemo: true,
    };
    writeAccounts(accounts);
  }
  if (!localStorage.getItem(dbKey(DEMO_CREDENTIALS.username))) {
    const snapshot = cache;
    const snapshotOwner = cacheOwner;
    cache = buildSeed();
    cacheOwner = DEMO_CREDENTIALS.username;
    persist();
    cache = snapshot;
    cacheOwner = snapshotOwner;
  }
}

ensureDemoAccount();

export default localApi;
